import * as React from 'react'
import { Search, X } from 'lucide-react'
import { Card as ShadCard } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useBranding } from '@/contexts/BrandingContext'

// ─── Card ─────────────────────────────────────────────────────────────────────

export function Card({
  children,
  style,
  className,
}: {
  children: React.ReactNode
  style?: object
  className?: string
}) {
  return (
    <ShadCard className={cn('gap-0 rounded-xl p-3.5', className)} style={style as React.CSSProperties}>
      {children}
    </ShadCard>
  )
}

export function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="mt-1 mb-2 flex items-center justify-between">
      {/* Callers write these in capitals, so they are styled as small labels:
          capitals at heading size read as shouting. */}
      <h3 className="text-[11.5px] font-semibold tracking-[0.06em] text-muted-foreground">{title}</h3>
      {action}
    </div>
  )
}

// ─── Badge ────────────────────────────────────────────────────────────────────

export type BadgeTone = 'neutral' | 'good' | 'warn' | 'bad' | 'info'

const TONES: Record<BadgeTone, { bg: string; fg: string }> = {
  neutral: { bg: '#f1f5f9', fg: '#64748b' },
  good: { bg: '#ecfdf5', fg: '#059669' },
  warn: { bg: '#fffbeb', fg: '#b45309' },
  bad: { bg: '#fef2f2', fg: '#dc2626' },
  info: { bg: '#eff6ff', fg: '#1d4ed8' },
}

export function Badge({ label, tone = 'neutral' }: { label: string; tone?: BadgeTone }) {
  const c = TONES[tone]
  return (
    <span
      className="inline-flex self-start rounded-md px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: c.bg, color: c.fg }}
    >
      {label}
    </span>
  )
}

// Booking status → tone, used everywhere a status is shown so colours stay stable.
export function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'completed': return 'good'
    case 'booked': return 'info'
    case 'cancelled': return 'bad'
    // Shares 'bad' with cancelled: both are "the session did not happen". The
    // badge LABEL carries the distinction here, and where colour is the only
    // signal — the calendar and charts — did_not_show gets its own hue
    // (STATUS_COLORS, STATUS_HUE). Don't move it to 'warn': that collides with
    // rescheduled, which only trades one indistinguishable pair for another.
    case 'did_not_show': return 'bad'
    case 'rescheduled': return 'warn'
    default: return 'neutral'
  }
}

// ─── Key/value rows ───────────────────────────────────────────────────────────

export function FieldRow({
  label,
  value,
  mono,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className="flex items-start gap-3 border-b border-muted py-[7px]">
      <span className="w-[132px] shrink-0 text-[12.5px] text-muted-foreground">{label}</span>
      {typeof value === 'string' || typeof value === 'number' ? (
        <span
          className={cn(
            'min-w-0 flex-1 text-[13.5px] font-medium text-foreground',
            mono && 'font-mono text-[12px]',
          )}
        >
          {value === '' || value === null ? '—' : String(value)}
        </span>
      ) : (
        <div className="min-w-0 flex-1">
          {value ?? <span className="text-[13.5px] font-medium text-foreground">—</span>}
        </div>
      )}
    </div>
  )
}

// ─── Stat tile ────────────────────────────────────────────────────────────────

export function StatTile({
  label,
  value,
  hint,
  tone,
  onPress,
}: {
  label: string
  value: string | number
  hint?: string
  tone?: BadgeTone
  onPress?: () => void
}) {
  const colour = tone ? TONES[tone].fg : '#0f172a'
  const tile =
    'flex min-w-[132px] grow basis-[132px] flex-col rounded-xl border border-border bg-card p-3.5 text-left shadow-sm'
  const body = (
    <>
      <span className="line-clamp-2 text-[11.5px] font-semibold text-muted-foreground">{label}</span>
      <span className="mt-1 truncate text-[21px] leading-tight font-bold" style={{ color: colour }}>{value}</span>
      {hint ? <span className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground/70">{hint}</span> : null}
    </>
  )
  if (!onPress) return <div className={tile}>{body}</div>
  return <button type="button" className={cn(tile, 'cursor-pointer')} onClick={onPress}>{body}</button>
}

// ─── Filter chips ─────────────────────────────────────────────────────────────

export function FilterChips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { key: T; label: string }[]
  value: T
  onChange: (key: T) => void
}) {
  // A segmented control, not filled pills: filled brand-colour pills made every
  // filter louder than the data it was filtering.
  return (
    <div className="inline-flex max-w-full flex-wrap items-center gap-0.5 self-start rounded-lg bg-muted p-[3px]">
      {options.map((o) => {
        const active = value === o.key
        return (
          <button
            key={o.key}
            type="button"
            className={cn(
              'cursor-pointer rounded-md px-2.5 py-1 text-[13px] font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground',
              'focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none',
              active && 'bg-background text-foreground shadow-xs',
            )}
            aria-pressed={active}
            onClick={() => onChange(o.key)}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// ─── Search box ───────────────────────────────────────────────────────────────

export function SearchInput({
  value,
  onChange,
  placeholder,
  onSubmit,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  onSubmit?: () => void
}) {
  return (
    // data-slot lets a toolbar row grow this to fill its line; anywhere else it
    // is a plain full-width box.
    <div
      data-slot="search"
      className="flex h-9 w-full items-center gap-2 rounded-lg border border-input bg-background px-2.5 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
    >
      <Search size={15} className="shrink-0 text-muted-foreground" />
      <Input
        className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] text-foreground shadow-none focus-visible:ring-0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? 'Search…'}
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit?.() }}
        enterKeyHint="search"
        autoCapitalize="none"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="shrink-0 cursor-pointer text-muted-foreground"
        >
          <X size={15} />
        </button>
      ) : null}
    </div>
  )
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { key: T; label: string; count?: number }[]
  value: T
  onChange: (k: T) => void
}) {
  const branding = useBranding()
  return (
    <div data-slot="tabs" className="flex flex-wrap gap-x-1 border-b border-border">
      {tabs.map((t) => {
        const active = value === t.key
        return (
          <button
            key={t.key}
            type="button"
            className={cn(
              'cursor-pointer border-b-2 border-b-transparent px-2.5 py-2.5 text-[13.5px] font-medium text-muted-foreground transition-colors hover:text-foreground',
              active && 'font-semibold',
            )}
            style={active ? { borderBottomColor: branding.primaryColor, color: branding.primaryColor } : undefined}
            aria-current={active ? 'true' : undefined}
            onClick={() => onChange(t.key)}
          >
            {t.label}{t.count !== undefined ? ` (${t.count})` : ''}
          </button>
        )
      })}
    </div>
  )
}

// ─── Empty / hint states ──────────────────────────────────────────────────────

export function EmptyNote({ text, hint }: { text: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-1 px-6 py-10">
      <p className="text-center text-[14px] text-muted-foreground">{text}</p>
      {hint ? <p className="text-center text-[12px] text-muted-foreground/70">{hint}</p> : null}
    </div>
  )
}
